<template>
  <div class="content-builder">
    <div class="form-group">
      <label class="control-label" for="instructions">Instructions</label>
      <QuillEditor v-model:content="content.instructions" :contentType="'html'" :modules="editorModules"
                   :toolbar="toolbarOpts"
                   theme="snow"/>
    </div>

    <div class="form-group">
      <label class="control-label">Loading Quotes</label>
      <textarea v-model="content.loadingQuotes" class="form-control" style="height: 150px;"
                placeholder="Analyzing your responses with care...&#10;Gathering insights from your work..."></textarea>
      <small class="form-text text-muted">Add phrases that will be shown while the AI analysis is being generated. One
        quote per line.</small>
    </div>

    <assistant-selector :id="content.assistantId" :type="'meta'" class="mt-4"
                        @handle-change="content.assistantId = $event"></assistant-selector>

    <div class="form-group">
      <label class="control-label">Meta Assistant Prompt</label>
      <textarea v-model="content.promptMetaAssistant" class="form-control"
                style="height: 200px;"></textarea>
      <small class="form-text text-muted">Provide detailed instructions on how you'd like the
        AI to evaluate the team's performance. This is also where you can set character limits for
        feedback (i.e. Please limit feedback to 500 characters or
        less).</small>
    </div>
    <hr/>
    <div class="form-group">
      <label>Meta Analysis Page IDs Filter</label>
      <small class="form-text text-muted">If you would like to filter the feedback based on specific pages, add pages
        you want to include. If left blank, feedback will be evaluated based on all
        pages.</small>

      <ol v-if="content.metaPageIdFilters.length > 0" class="list-group mt-4">
        <li v-for="(item, index) in content.metaPageIdFilters" class="list-group-item">
          <div class="row">
            <div class="col-5">
              {{ item.name }}
            </div>
            <div class="col-5">
              <div>
                <input :id="'fbdata-' + index" v-model="item.feedbackAssistants" type="checkbox"/>
                <label :for="'fbdata-' + index" class="ml-2">Feedback Assistant Data</label>
              </div>
              <div>
                <input :id="'sadata-' + index" v-model="item.scoringAssistants" type="checkbox"/>
                <label :for="'sadata-' + index" class="ml-2 mb-0">Scoring Assistant Data</label>
              </div>
            </div>
            <div class="col-2">
              <button aria-label="remove page filter" class="btn btn-danger ml-auto d-flex" type="button"
                      v-on:click="removePageFilter(content.metaPageIdFilters, index)"><i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </li>
      </ol>
      <div class="row my-4">
        <div class="col-10">
          <page-selector :id-index="'page-lookup-1'" @handle-change="selected = $event" class="mb-3"></page-selector>
        </div>
        <div class="col-2 d-flex align-items-center">
          <button class="btn btn-primary" type="button" v-on:click="addPageFilter(content.metaPageIdFilters)">Add
            Filter
          </button>
        </div>
      </div>
    </div>
    <hr/>
    <div class="row">
      <div class="col-12 mb-4">
        <label class="control-label">Assistant Sentiment Analysis</label>
        <small class="form-text text-muted">To perform a sentiment analysis of assistants used in the simulation,
          add and configure assistants here. If no assistants are added, a general analysis will be performed.</small>
      </div>
      <div class="col-2">
        <button class="btn btn-primary mb-4" type="button" @click="addAssistant()">Add Assistant</button>
      </div>
    </div>
    <template v-if="content.assistants.length > 0">
      <ul class="list-group my-4">
        <li v-for="(assistant, index) in content.assistants" :key="index" class="list-group-item">
          <div class="d-flex">
            <div class="col-3">
              <img v-if="assistant.image" :src="assistant.image" alt="" class="img-thumbnail mb-2">
              <label :for="`assistant-image-${index}`" class="control-label">Assistant Image</label>
              <div class="input-group">
                <div class="input-group-prepend">
                  <a :data-input="`assistant-image-${index}`" class="btn btn-primary text-white" data-lfm="image">
                    <i class="fa fa-image"></i>
                  </a>
                </div>
                <input :id="`assistant-image-${index}`" v-model="assistant.image" class="form-control" type="text">
              </div>
              <div class="form-group">
                <input :id="`tts-${index}`" v-model="assistant.tts" type="checkbox">
                <label :for="`tts-${index}`" class="ml-1">Enable Text to Speech</label>
              </div>
              <div v-if="assistant.tts" class="form-group">
                <label :for="`voice-${index}`" class="sr-only">Voice</label>
                <select :id="`voice-${index}`" v-model="assistant.voice" class="form-control">
                  <option value="alloy">Alloy</option>
                  <option value="echo">Echo</option>
                  <option value="fable">Fable</option>
                  <option value="onyx">Onyx</option>
                  <option value="nova">Nova</option>
                  <option value="shimmer">Shimmer</option>
                </select>
              </div>
            </div>
            <div class="col-9">
              <label :for="`assistant-label-${index}`" class="control-label">Assistant Label</label>
              <input :id="`assistant-label-${index}`" v-model="assistant.label" class="form-control mb-4" type="text">
              <assistant-selector :id="assistant.id" :idIndex="`${index + 1}`" :type="'feedback'"
                                  @handle-change="assistant.id = $event"></assistant-selector>
              <label :for="`assistant-prompt-${index}`" class="control-label">Feedback Prompt</label>
              <textarea :id="`assistant-prompt-${index}`" v-model="assistant.prompt" class="form-control mb-4"
                        style="height: 200px;"></textarea>
            </div>
          </div>
          <button aria-label="remove assistant" class="btn btn-danger position-absolute btn-remove" type="button"
                  @click="removeAssistant(index)"><i class="fa fa-trash"></i>
          </button>
        </li>
      </ul>
    </template>
    <div class="form-group">
      <label>Sentiment Analysis Page IDs Filter</label>
      <small class="form-text text-muted">If you would like to filter the feedback based on specific pages, add pages
        you want to include. If left blank, feedback will be evaluated based on all pages.</small>
      <ol v-if="content.sentimentPageIdFilters.length > 0" class="list-group mt-4">
        <li v-for="(item, index) in content.sentimentPageIdFilters" class="list-group-item">
          <div class="row">
            <div class="col-5">
              {{ item.name }}
            </div>
            <div class="col-5">
            </div>
            <div class="col-2">
              <button aria-label="remove page filter" class="btn btn-danger ml-auto d-flex" type="button"
                      v-on:click="removePageFilter(content.sentimentPageIdFilters, index)"><i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </li>
      </ol>
      <div class="row my-4">
        <div class="col-10">
          <page-selector :id-index="'page-lookup-2'" @handle-change="selected = $event" class="mb-3"></page-selector>
        </div>
        <div class="col-2 d-flex align-items-center">
          <button class="btn btn-primary" type="button" v-on:click="addPageFilter(content.sentimentPageIdFilters)">Add
            Filter
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import _ from 'lodash'
import layoutMixin from '../../mixins/layout'
import {QuillEditor} from '@vueup/vue-quill'
import AssistantSelector from '../AssistantSelector.vue'
import PageSelector from '../PageSelector.vue'
import {EDITOR_CONFIG_BUTTONS_FULL, EDITOR_CONFIG_LITE} from "../../../common/constants";

export default {
  components: {
    QuillEditor,
    AssistantSelector,
    PageSelector
  },
  mixins: [layoutMixin],
  data() {
    const content = _.cloneDeep(this.source)
    const filled = _.defaults(content, {
      instructions: '',
      promptMetaAssistant: '',
      assistants: [],
      metaPageIdFilter: '',
      metaPageIdFilters: [],
      sentimentPageIdFilter: '',
      sentimentPageIdFilters: [],
      selected: null,
      loadingQuotes: 'Processing...'
    })
    return {
      content: filled,
      toolbarOpts: EDITOR_CONFIG_BUTTONS_FULL,
      editorOptsLite: EDITOR_CONFIG_LITE
    }
  },
  methods: {
    getValue() {
      return this.content
    },
    addAssistant() {
      this.content.assistants.push({
        id: null,
        image: null,
        label: '',
        prompt: '',
        tts: false,
        voice: 'alloy'
      })
    },
    removeAssistant(index) {
      this.content.assistants.splice(index, 1)
    },
    addPageFilter(filterList) {
      console.log('add page')
      if (this.selected) {
        console.log('selected', this.selected)
        if (!filterList.some((item) => item.id === this.selected.id)) {
          filterList.push({
            name: this.selected.text,
            id: this.selected.id,
            feedbackAssistants: true,
            scoringAssistants: true
          })
        }
      }
    },
    removePageFilter(filterList, index) {
      filterList.splice(index, 1)
    }
  },
  mounted: async function () {
  }
}

</script>
